import React from 'react';
import {
  Notification,
  NotificationType,
  getNotifications,
  getUnreadCount,
  markAllRead,
  setEntityPreference,
} from './api';
import { getPublicProfile } from '../social/api';

/**
 * Inbox state for the nav bell (D-39).
 *
 * POLLED, not pushed: there is no WebSocket delivery for notifications yet
 * (T3.3 is partial — the store and triggers exist, delivery doesn't), so the
 * badge is refreshed on an interval and the list is refreshed when the user
 * actually looks at it.
 *
 * Those two are split on purpose. `unread-count` reads ONE counter row, so it
 * is cheap enough to poll every minute; the inbox is a query plus a profile
 * lookup per distinct actor, so it only runs on open. Polling the list at the
 * same rate would multiply the cost of a badge nobody clicked.
 */

/**
 * Badge polling, and why it is not a plain `setInterval`.
 *
 * At 100k users a naive 60s poll is ~1,667 req/s sustained, 24/7, of which
 * ~99.5% return "nothing changed" — a typical user receives a handful of
 * notifications a day and polls 1,440 times. Three cheap corrections, in
 * descending order of what they save:
 *
 *  1. HIDDEN TABS DON'T POLL. Most open tabs are not the foreground tab. The
 *     request is skipped entirely while hidden and fires immediately on return,
 *     so the user never sees a stale badge.
 *  2. IDLE BACKOFF. Unchanged count → widen 60s → 2m → 5m. Any change, or the
 *     tab regaining focus, snaps back to the floor. An idle user costs ~1/5th.
 *  3. JITTER. Every interval is ±25% randomised. Without it, clients that
 *     loaded together (a deploy, a morning rush, a recovered outage) stay in
 *     lockstep forever and arrive as a synchronised spike — the thundering-herd
 *     failure mode, where correlated load vastly exceeds steady-state capacity.
 *     This is the same reason `withTransactionRetry` jitters its backoff.
 *
 * None of this changes what the user sees. The real fix is push (T3.3
 * delivery), which deletes this loop rather than tuning it.
 */
const BADGE_POLL_FLOOR_MS = 60_000;
// The product SLO is "notification state at most 5 minutes stale" (set
// 2026-08-03). The ceiling is therefore 240s, NOT 300s: jitter stretches an
// interval by up to +25%, and 240 × 1.25 = 300s lands exactly on the bound.
// A 300s ceiling would jitter out to 375s and quietly violate the SLO on the
// slow tail — the worst CASE has to honor the promise, not the average.
const BADGE_POLL_CEILING_MS = 240_000;
const JITTER_RATIO = 0.25;

/** ±25% so a fleet of clients spreads instead of arriving together. */
const withJitter = (ms: number): number =>
  Math.round(ms * (1 + (Math.random() * 2 - 1) * JITTER_RATIO));

const INBOX_LIMIT = 10;

export interface NotificationView extends Notification {
  /** Resolved display name for `actorId`, or a neutral fallback. */
  actorName: string;
}

/** "Maya", "Maya and 4 others" — the aggregate row's whole reason to exist. */
export const actorLabel = (view: NotificationView): string =>
  view.actorCount > 1
    ? `${view.actorName} and ${view.actorCount - 1} other${view.actorCount > 2 ? 's' : ''}`
    : view.actorName;

export const describeNotification = (view: NotificationView): string => {
  const who = actorLabel(view);
  switch (view.type) {
    case 'post.like': return `${who} liked your post`;
    case 'post.comment': return `${who} commented on your post`;
    case 'trip.like': return `${who} liked your trip`;
    case 'trip.comment': return `${who} commented on your trip`;
    case 'post.created': return `${who} shared a new post`;
    case 'trip.published': return `${who} published a new trip`;
    case 'user.followed': return `${who} followed you`;
    case 'user.follow_requested': return `${who} asked to follow you`;
    case 'user.follow_accepted': return `${who} accepted your follow request`;
    default: return `${who} sent you an update`;
  }
};

/**
 * What "turn this off" means for a given row — the thing whose future
 * notifications the user actually wants to stop.
 *
 * Keyed on the TYPE, not on `entityType`, because those disagree in the case
 * that matters. `trip.published` carries a trip, but the row exists because you
 * follow its AUTHOR: muting the trip would silence one trip and leave the
 * firehose on. Only a thread you are being notified ABOUT — likes and comments
 * on a specific trip — is a trip-shaped mute.
 *
 * Returns null when there is no unambiguous target: an AGGREGATED row has many
 * actors, and muting whichever one happened to be most recent would silence
 * almost none of the noise while looking like it had worked. The category
 * switch in settings is the honest control for that case.
 */
const TRIP_THREAD_TYPES = new Set<NotificationType>(['trip.like', 'trip.comment']);

export interface MuteTarget {
  entityType: 'trip' | 'user';
  entityId: string;
  label: string;
}

export const muteTargetFor = (view: NotificationView): MuteTarget | null => {
  if (TRIP_THREAD_TYPES.has(view.type)) {
    return { entityType: 'trip', entityId: view.entityId, label: 'Mute this trip' };
  }
  if (view.aggregated || !view.actorId) return null;
  return { entityType: 'user', entityId: view.actorId, label: `Mute ${view.actorName}` };
};

export const muteKey = (target: MuteTarget): string =>
  `${target.entityType}:${target.entityId}`;

/**
 * Where clicking a notification should land.
 *
 * The graph rows split on whether they aggregate, and `entityId` differs to
 * match: an aggregate ("5 people followed you") points at YOU, so the useful
 * destination is your own connections page — where you would act on them; a
 * single row ("Alice accepted you") points at the ACTOR, and the only thing it
 * is about is that person.
 */
export const notificationHref = (view: Notification): string => {
  if (view.entityType === 'trip') return `/trip/${view.entityId}`;
  if (view.entityType === 'user') {
    return view.aggregated ? '/connections' : `/user/${view.entityId}`;
  }
  return '/feed';
};

export function useNotifications() {
  const [notifications, setNotifications] = React.useState<NotificationView[]>([]);
  /** `type:id` of everything muted THIS session — drives the button label only. */
  const [muted, setMuted] = React.useState<Set<string>>(() => new Set());
  const [unread, setUnread] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const refreshBadge = React.useCallback(async () => {
    try {
      setUnread(await getUnreadCount());
    } catch {
      // A failed badge poll is not worth surfacing — the next tick retries.
    }
  }, []);

  const refreshInbox = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const page = await getNotifications({ limit: INBOX_LIMIT });

      // One profile lookup per DISTINCT actor, not per row: a burst of likes on
      // one post aggregates to a single row, but several rows can still share
      // an actor.
      const actorIds = Array.from(new Set(page.notifications.map((item) => item.actorId).filter(Boolean)));
      const profiles = new Map<string, string>();
      await Promise.all(actorIds.map(async (actorId) => {
        const profile = await getPublicProfile(actorId).catch(() => null);
        const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
        profiles.set(actorId, name || profile?.nickname || 'Someone');
      }));

      setNotifications(page.notifications.map((item) => ({
        ...item,
        actorName: profiles.get(item.actorId) || 'Someone',
      })));
      setError(null);
    } catch (err: any) {
      // 503 = the notifications table isn't wired up in this environment yet.
      setError(err?.status === 503 ? null : 'Could not load notifications.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;
    let delay = BADGE_POLL_FLOOR_MS;
    let lastSeen = -1;

    // setTimeout, not setInterval: the delay has to change between ticks, and
    // an interval whose handler is slower than its period stacks up.
    const schedule = () => {
      if (!stopped) timer = setTimeout(tick, withJitter(delay));
    };

    const tick = async () => {
      if (stopped) return;

      // Skip the request while hidden, but KEEP the loop alive so returning to
      // the tab resumes on the existing cadence rather than cold-starting.
      if (document.visibilityState === 'visible') {
        const count = await getUnreadCount().catch(() => null);
        if (count !== null) {
          setUnread(count);
          // Something arrived → the user is in an active window, poll tight.
          // Nothing did → widen, because an idle inbox is the common case.
          delay = count !== lastSeen
            ? BADGE_POLL_FLOOR_MS
            : Math.min(delay * 2, BADGE_POLL_CEILING_MS);
          lastSeen = count;
        }
      }
      schedule();
    };

    // Returning to the tab is the strongest signal that the badge matters right
    // now: check immediately and reset the backoff.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      delay = BADGE_POLL_FLOOR_MS;
      clearTimeout(timer);
      void tick();
    };

    void tick();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const markAllAsRead = React.useCallback(async () => {
    // Optimistic: the badge is a derived number, and a failed write just means
    // the next poll puts it back.
    setUnread(0);
    setNotifications((current) => current.map((item) => ({
      ...item,
      readAt: item.readAt || new Date().toISOString(),
    })));
    try {
      await markAllRead();
    } catch {
      void refreshBadge();
    }
  }, [refreshBadge]);

  /**
   * Silence future notifications about one trip or one person.
   *
   * Optimistic and NOT undone on failure: the muted set only drives this
   * session's button label, and the authoritative list is re-read from the
   * server every time settings opens. Rolling back would flicker a button the
   * user has already stopped looking at; a failed mute simply isn't reflected
   * next time, where the real list is shown.
   *
   * Existing rows are deliberately left in place — mute means "no MORE of
   * this", not "erase what already happened". Retroactively deleting an inbox
   * the user has already seen is a different and more surprising action.
   */
  const muteEntity = React.useCallback(async (target: MuteTarget) => {
    setMuted((current) => new Set(current).add(muteKey(target)));
    try {
      await setEntityPreference(target.entityType, target.entityId, true);
    } catch {
      // Settings holds the truth; see above.
    }
  }, []);

  return {
    notifications,
    unread,
    isLoading,
    error,
    refreshInbox,
    refreshBadge,
    markAllAsRead,
    muted,
    muteEntity,
  };
}
